import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import { get, keyBy } from 'lodash';
import { Row, Col, Select } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import {
  FormTitle,
  StyledCol,
  StyledFormItem,
  StyledSelect,
  StyledDatePicker,
  StyledButton
} from 'components/Styled';

import { addReschedulePickupRequest } from '../../../../ducks/users';

const { Option } = Select;

const ReschedulePickup = props => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { t } = useTranslation();
  const location = useLocation();

  const rescheduleSuccess = useSelector(
    state => state.users.addReschedulePickupSuccess
  );
  const loading = useSelector(state => !!state.users.loading);

  const [pickupDate, setPickupDate] = useState('');
  const [pickupTime, setPickupTime] = useState('');
  const [pickupTimes, setPickupTimes] = useState([]);
  const [error, setError] = useState({});

  const refNumber = props.match.params.ref;

  useEffect(() => {
    if (location.state === undefined) {
      history.push(`${process.env.PUBLIC_URL}/account/orders`);
    }

    if (rescheduleSuccess === undefined) return;
    if (rescheduleSuccess) {
      history.push({
        pathname: `${process.env.PUBLIC_URL}/account/orders/${refNumber}`,
        state: { reschedulePickupSuccess: true }
      });
    }
  }, [rescheduleSuccess]);

  const disabledDate = date => {
    const availableDates = location.state.timeDate;
    return availableDates.every(x => !(x.date === date.format('YYYY-MM-DD')));
  };

  const handlePickerOpen = open => {
    if (open) {
      setTimeout(() => {
        const input = document.querySelector('.ant-picker-input input');
        input.readOnly = true; // to prevent keyboard popup
      }, 0);
    }
  };

  const handleInputChange = (value, type) => {
    setError({});

    if (type === 'pickupDate') {
      const availableDates = location.state.timeDate;
      const selectedDate = moment(value).format('YYYY-MM-DD');
      const pickupTimes =
        keyBy(availableDates, 'date')[selectedDate]['time'] || [];
      setPickupTimes(pickupTimes);

      setPickupDate(value);
    } else if (type === 'pickupTime') {
      setPickupTime(value);
    }
  };

  const getFormData = () => {
    const getPickupDate = pickupDate;
    const getPickupTime = pickupTime;

    return {
      pickupDate: moment(getPickupDate).format('YYYY-MM-DD'),
      minPickupTime: get(pickupTimes, `[${getPickupTime}].from`),
      maxPickupTime: get(pickupTimes, `[${getPickupTime}].to`)
    };
  };

  const handleSubmit = () => {
    window.dataLayer.push({
      event: 'Click Confirm Pick Up Reschedule Button'
    });

    const data = getFormData();

    if (data.pickupDate === 'Invalid date') {
      setError({
        pickupDate: t('pickup.err_msg.pickupDate.empty'),
        pickupTime: t('pickup.err_msg.pickupTime.empty')
      });
    } else if (
      data.minPickupTime === undefined ||
      data.maxPickupTime === undefined
    ) {
      setError({
        pickupTime: t('pickup.err_msg.pickupTime.empty')
      });
    } else {
      dispatch(
        addReschedulePickupRequest({
          ref: refNumber,
          date: data.pickupDate,
          time: {
            from: data.minPickupTime,
            to: data.maxPickupTime
          }
        })
      );
    }
  };

  return (
    <div
      style={{
        margin: '0 auto',
        width: '100%',
        maxWidth: `${process.env.REACT_APP_MAX_WIDTH}px`
      }}>
      <div
        style={{
          padding: '31px 11px',
          height: '64px',
          borderBottom: '1px solid #D9DFEC'
        }}>
        <Row type="flex" align="bottom">
          <div>
            <span
              className="back-button"
              style={{
                color: '#2C2C2E',
                fontSize: '16px',
                cursor: 'pointer',
                padding: '30px 0 30px'
              }}
              onClick={() => {
                history.push(`${process.env.PUBLIC_URL}/account/orders`);
              }}>
              <FontAwesomeIcon icon="angle-left" />
            </span>
          </div>
          <Col
            span={23}
            style={{
              fontSize: '16px',
              textAlign: 'center'
            }}>
            {t('reschedule_pickup.title')}
          </Col>
        </Row>
      </div>

      <div
        style={{
          padding: '15px',
          height: '135px',
          borderBottom: '1px solid #D9DFEC',
          boxShadow: '0 3px 6px 0 rgba(0, 0, 0, 0.2)'
        }}>
        <Row type="flex" gutter={14} style={{ paddingTop: '20px' }}>
          <StyledCol>
            <FormTitle>{t('reschedule_pickup.pickup_date')}</FormTitle>
            <StyledFormItem
              validateStatus={error.pickupDate ? 'error' : ''}
              help={error.pickupDate}>
              <StyledDatePicker
                allowClear={false}
                size="large"
                onOpenChange={handlePickerOpen}
                placeholder={t('common.select')}
                value={pickupDate || null}
                onChange={value => {
                  handleInputChange(value, 'pickupDate');
                }}
                disabled={loading}
                disabledDate={disabledDate}
              />
            </StyledFormItem>
          </StyledCol>
          <StyledCol>
            <FormTitle>{t('reschedule_pickup.pickup_time')}</FormTitle>
            <StyledFormItem
              validateStatus={error.pickupTime ? 'error' : ''}
              help={error.pickupTime}>
              <StyledSelect
                size="large"
                placeholder={t('common.select')}
                value={pickupTime}
                disabled={loading}
                onChange={value => {
                  handleInputChange(value, 'pickupTime');
                }}>
                {pickupTimes
                  ? pickupTimes.map((time, i) => (
                      <Option key={i} value={i}>
                        {time.from} - {time.to}{' '}
                        {time.extra ? `(${time.extra})` : ''}
                      </Option>
                    ))
                  : ''}
              </StyledSelect>
            </StyledFormItem>
          </StyledCol>
        </Row>
      </div>
      <div style={{ padding: '20px 15px' }}>
        <StyledButton
          type="primary"
          shadow="true"
          onClick={handleSubmit}
          loading={loading}>
          {t('reschedule_pickup.confirm_reschedule')}
        </StyledButton>
      </div>
    </div>
  );
};

export default ReschedulePickup;
